import { EyeIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { useEffect, useState } from 'react'
import { getEmailLogDetails, getEmailLogs } from 'services/apis/admin/admin'
import { Input2, Pagination, PlainTable } from 'stories/components'
import { formatTime } from 'utils'

import { EmailLogDetail } from './EmailLogDetail'
import { EmailLog } from './type'

const itemCountPerPage = 10

export const EmailLogs = () => {
  const [filters, setFilters] = useState<Record<string, any>>({
    query: '',
    orderBy: 'createdAt',
    orderDir: '-1',
    pageNum: 0,
  })
  const [filterQuery, setFilterQuery] = useState(filters.query)

  const [logs, setLogs] = useState<EmailLog[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [total, setTotal] = useState<number>(0)
  const [isGetLogsOnce, setIsGetLogsOnce] = useState(false)
  const [selectedLog, setSelectedLog] = useState<EmailLog | null>(null)
  const [mode, setMode] = useState<string>('')

  const fetchLogs = async (filters: Record<string, any>) => {
    setIsLoading(true)
    try {
      const res = await getEmailLogs({
        query: filters.query.trim(),
        page: filters.pageNum,
        limit: itemCountPerPage,
        orderBy: filters.orderBy,
        orderDir: filters.orderDir,
      })
      setLogs(res.data || [])
      setTotal(res.total || 0)
    } catch (error) {
      console.error('Failed to fetch email logs:', error)
      setLogs([])
      setTotal(0)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (!isGetLogsOnce) return
    const timeOutId = setTimeout(() => {
      if (!isLoading) {
        const newFilters = cloneDeep(filters)
        newFilters.query = filterQuery
        newFilters.pageNum = 0
        setFilters(newFilters)
        fetchLogs(newFilters)
      }
    }, 700)
    return () => clearTimeout(timeOutId)
  }, [filterQuery])

  useEffect(() => {
    fetchLogs(filters).then(() => {
      setIsGetLogsOnce(true)
    })
  }, [])

  const onChangeFilter = (key: string, value: any) => {
    const newFilters = cloneDeep(filters)
    newFilters[key] = value
    setFilters(newFilters)
    if (key === 'query') setFilterQuery(value)
    else fetchLogs(newFilters)
  }

  const onPageNavigate = (num: number) => {
    onChangeFilter('pageNum', num)
  }

  const handleViewDetail = async (log: EmailLog) => {
    setIsLoading(true)
    try {
      const res = await getEmailLogDetails(log.id)
      setSelectedLog(res)
      setMode('view')
    } catch (error) {
      console.error('Failed to fetch email detail:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const onBack = () => {
    setMode('')
    setSelectedLog(null)
  }

  return (
    <div className="email-logs-container">
      <LayoutLoading show={isLoading} />
      {mode === 'view' && selectedLog ? (
        <EmailLogDetail log={selectedLog} onBack={onBack} />
      ) : (
        <div>
          <h2 className="text-2xl font-semibold mb-4">Email Logs</h2>
          <div className="mb-4">
            <Input2
              value={filterQuery}
              type="search"
              title="Search Email Logs"
              onChange={(value) => setFilterQuery(value)}
              className="w-120"
            />
          </div>

          <PlainTable
            header={['No', 'To', 'Subject', 'Sent At', 'Action']}
            data={logs.map((log, index) => [
              filters.pageNum * itemCountPerPage + index + 1,
              <div className="max-w-xs overflow-hidden text-ellipsis" title={log.to.join(', ')}>
                {log.to.join(', ')}
              </div>,
              <div className="max-w-xs overflow-hidden text-ellipsis" title={log.subject}>
                {log.subject}
              </div>,
              formatTime(log.createdAt, 'MM/dd/yyyy HH:mm:ss'),
              <div className="flex">
                <span
                  className="text-shade-blue p-1 hover-shadow1 cursor-pointer"
                  onClick={() => handleViewDetail(log)}
                >
                  <EyeIcon className="w-4 h-4"></EyeIcon>
                </span>
              </div>,
            ])}
          />

          <div className="mt-4 flex justify-center">
            <Pagination
              totalCount={total}
              itemCountPerPage={itemCountPerPage}
              pageNum={filters.pageNum}
              onNavigate={onPageNavigate}
            />
          </div>
        </div>
      )}
    </div>
  )
}
